<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

import type { ReviewsFragment } from '@/graphql/api'

import { parseDateTime } from '@/utils/date.ts'
import { useDateFormat } from '@/composables/useDateFormat'
import { getGenericImageProps } from '@/components/useImageProps'

import Image from '@/components/Image.vue'
import ReviewRating from './ReviewRating.vue'

defineProps<{
    data: ReviewsFragment[]
}>()

const { t } = useI18n()

const formatDate = useDateFormat({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
})
</script>

<template>
    <div
        v-if="data.length"
        id="product-reviews"
        class="product-reviews"
    >
        <div class="product-reviews-wrapper">
            <div class="product-reviews-title">
                <h3 class="h3">
                    {{ t('reviews.title', { count: data.length }) }}
                </h3>
            </div>

            <div class="product-review-list">
                <div
                    v-for="review in data"
                    :key="review.id"
                    class="product-review"
                >
                    <div class="product-review-meta">
                        <ReviewRating :rating="review.rating" />

                        <div class="product-review-date">
                            {{ formatDate(parseDateTime(review.date)) }}
                        </div>
                    </div>

                    <div class="product-review-content">
                        <div class="product-review-text">
                            <blockquote class="blockquote">
                                <p class="paragraph">
                                    {{ review.comment.replace(/\r\n/g, '\n') }}
                                </p>

                                <cite class="product-review-author">
                                    {{ review.name }}
                                    <span class="verified-buyer-label">
                                        [{{ t('reviews.verified_buyer') }}]
                                    </span>
                                </cite>
                            </blockquote>
                        </div>

                        <div
                            v-if="review.picture"
                            class="product-review-image"
                        >
                            <Image
                                v-bind="
                                    getGenericImageProps({
                                        size: review.picture.small,
                                        size_x2: review.picture.small_x2,
                                    })
                                "
                                :alt="`Image by ${review.name}`"
                                class="image"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.product-reviews {
    width: 100%;
    padding-bottom: 60px;

    .product-reviews-wrapper {
        width: 720px;
        max-width: 100%;
        padding: 0 20px;
        margin: 0 auto;

        .product-reviews-title {
            width: 100%;
            padding: 20px 0 40px;

            .h3 {
                font-family: $parcel-font-stack;
                color: $blue;
                font-size: 24px;
                font-weight: 400;
                line-height: 1.2em;
            }
        }

        .product-review-list {
            width: 100%;
            padding: 20px 0;
            border-top: 1px solid $grey-300;

            .product-review {
                width: 100%;
                padding: 20px 0;
                border-bottom: 1px solid $grey-300;

                .product-review-meta {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 0 15px;

                    .product-review-date {
                        font-family: $effra-font-stack;
                        font-size: 11px;
                        text-transform: uppercase;
                        color: $slate;
                        letter-spacing: 0.05em;
                    }
                }

                .product-review-content {
                    .product-review-text {
                        flex-grow: 1;

                        .blockquote {
                            font-family: $sectra-font-stack;
                            font-size: 16px;
                            line-height: 1.2em;
                            color: $slate;
                            padding: 0 20px 0 0;

                            .paragraph {
                                padding: 0;
                                margin: 0 0 20px;
                                display: block;
                            }

                            .product-review-author {
                                display: block;
                                font-family: $effra-font-stack;
                                font-size: 14px;
                                line-height: 15px;
                                text-transform: uppercase;
                                letter-spacing: 0.05em;
                                font-style: normal;

                                .verified-buyer-label {
                                    display: block;
                                    font-family: $effra-font-stack;
                                    font-size: 11px;
                                    text-transform: uppercase;
                                    color: $grey-400;
                                    letter-spacing: 0.05em;
                                }
                            }
                        }
                    }

                    .product-review-image {
                        flex-shrink: 0;
                        flex-grow: 0;
                        width: 200px;
                        aspect-ratio: 1;
                        padding: 0 0 0 20px;

                        .image {
                            width: 100%;
                            height: 100%;
                            object-fit: cover;
                        }
                    }
                }
            }
        }
    }
}
</style>
